import { useEffect, useState } from 'react';
import { API_URL } from '@/components/Constants';
import { DEMO_DISEASES } from './demoComparisonData';
import type { DemoDiseaseId, DemoDiseaseStats } from './demoComparisonTypes';

type StatsByDisease = Partial<Record<DemoDiseaseId, { academic: DemoDiseaseStats; full: DemoDiseaseStats }>>;

async function fetchDiseaseStats(cui: string, source: 'ALL' | 'CURATED'): Promise<DemoDiseaseStats | null> {
  const params = new URLSearchParams({
    source,
    disease: `UMLS_${cui}`,
    page_number: '0',
    page_size: '10',
    skey: 'unauth',
  });

  try {
    const res = await fetch(`${API_URL}api/disease/all?${params.toString()}`, {
      headers: { accept: 'application/json' },
    });
    const json = await res.json();
    const row = json?.payload?.[0];
    if (json?.status !== 'OK' || !row) return null;
    return {
      genes: Number(row.numGenesAssociatedToDisease) || 0,
      variants: Number(row.numVariantsAssociatedToDisease) || 0,
      studies: Number(row.numPublications) || 0,
    };
  } catch {
    return null;
  }
}

export function useDemoDiseaseStats() {
  const [liveStats, setLiveStats] = useState<StatsByDisease>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const entries = await Promise.all(
        DEMO_DISEASES.map(async disease => {
          const [academic, full] = await Promise.all([
            fetchDiseaseStats(disease.cui, 'CURATED'),
            fetchDiseaseStats(disease.cui, 'ALL'),
          ]);
          if (!academic || !full) return null;
          return [disease.id, { academic, full }] as const;
        }),
      );

      if (!cancelled) {
        const next: StatsByDisease = {};
        for (const entry of entries) {
          if (entry) next[entry[0]] = entry[1];
        }
        setLiveStats(next);
        setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  function getStats(diseaseId: DemoDiseaseId): { academic: DemoDiseaseStats; full: DemoDiseaseStats } {
    const disease = DEMO_DISEASES.find(d => d.id === diseaseId)!;
    return liveStats[diseaseId] ?? disease.fallback;
  }

  return { getStats, loading };
}

export function formatPctIncrease(academic: number, full: number): string | null {
  if (academic <= 0 || full <= academic) return null;
  const pct = Math.round(((full - academic) / academic) * 100);
  return `+${pct}%`;
}
